import { forwardRef, useEffect, useImperativeHandle, useRef, useState } from "react";
import { vervangSmiley } from "../lib/emoticons";
import type { Conversation } from "@swift/shared";
import { api, uploadFile } from "../lib/api";
import { getSocket } from "../lib/socket";
import { useStore } from "../lib/store";
import { isCapacitor } from "../lib/capacitor";
import { isQueueableError, listOutbox, makeOutboxId, pushOutbox } from "../lib/outbox";
import EmojiPicker from "./EmojiPicker";
import MentionAutocomplete, { type MentionCandidate } from "./MentionAutocomplete";
import PollComposer from "./PollComposer";
import AttachmentSheet, { type AttachmentAction } from "./AttachmentSheet";
import {
  IconSmile,
  IconPaperclip,
  IconMic,
  IconStop,
  IconSend,
  IconCamera,
  IconFile,
  IconPoll,
  IconLocation,
  IconClose,
} from "./icons";

/** Camera-actie alleen tonen waar 'ie zinvol is: in de Android-app
 *  (Capacitor) of in een mobiele PWA-browser. Op desktop voegt 'ie niets
 *  toe boven de bestaande paperclip-knop. We detecteren mobiel via
 *  touchscreen-mediaquery — geen user-agent-sniffing. */
function isTouchPlatform(): boolean {
  if (isCapacitor()) return true;
  if (typeof window === "undefined") return false;
  return window.matchMedia?.("(pointer: coarse)").matches ?? false;
}

type Props = { conversation: Conversation; disabledReason?: string };

export type ComposerHandle = {
  addFiles(files: File[]): void;
  insertText(text: string): void;
};

type PendingAttachment = {
  id: number;
  filename: string;
  size: number;
};

function formatSeconds(s: number): string {
  const m = Math.floor(s / 60);
  const r = s % 60;
  return `${m}:${r.toString().padStart(2, "0")}`;
}

const Composer = forwardRef<ComposerHandle, Props>(function Composer(
  { conversation, disabledReason },
  ref,
) {
  const [text, setText] = useState("");
  const [pending, setPending] = useState<PendingAttachment[]>([]);
  const [sending, setSending] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [emojiOpen, setEmojiOpen] = useState(false);
  const textRef = useRef<HTMLTextAreaElement>(null);
  // Synchrone in-flight-guard naast de `sending`-state: state updatet pas
  // bij de volgende render, dus twee snelle Enters konden allebei langs de
  // check komen en het bericht dubbel versturen. Een ref is direct waar.
  const sendingRef = useRef(false);
  // Reply-target voor dit gesprek; null als de gebruiker niet aan 't
  // antwoorden is. setReplyTarget(conversation.id, null) wist 'm — de
  // Composer doet dat na verzending én bij de × in de banner.
  const replyTarget = useStore((s) => s.replyTargets[conversation.id] ?? null);
  const setReplyTarget = useStore((s) => s.setReplyTarget);

  // Mention-autocomplete state. `mentionAt` is de index in `text` van de
  // @ die de trigger startte; null als de popover dicht is. `mentionQuery`
  // is alles ná de @ tot de cursor — lowercased, voor matching.
  // `mentionIdx` is de cursor in de suggestie-lijst (toetsenbord-nav).
  // `mentionList` is de actuele suggestie-set; verschijnt vanuit de
  // child via onCandidatesChanged zodat Enter daar de juiste kandidaat
  // kan kiezen zonder de child rechtstreeks te bevragen.
  const [mentionAt, setMentionAt] = useState<number | null>(null);
  const [mentionQuery, setMentionQuery] = useState("");
  const [mentionIdx, setMentionIdx] = useState(0);
  const [mentionList, setMentionList] = useState<MentionCandidate[]>([]);
  const mentionOpen = mentionAt !== null && mentionList.length > 0;

  // Spraakberichten: status, timer en de live MediaRecorder. We bewaren
  // alles in refs zodat een interval-tick + de stop-handler niet hoeven
  // synchroniseren met React's render-cyclus. De gebruiker klikt 🎤 →
  // opname start → klik stop → de blob wordt als bijlage in `pending`
  // gezet, daarna kan ze nog tekst toevoegen of direct sturen.
  const [recording, setRecording] = useState(false);
  const [recordError, setRecordError] = useState<string | null>(null);
  const [recordSeconds, setRecordSeconds] = useState(0);
  const recorderRef = useRef<MediaRecorder | null>(null);
  const recordChunksRef = useRef<Blob[]>([]);
  const recordStreamRef = useRef<MediaStream | null>(null);
  const recordTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  // Cancel-flag voor "niet uploaden, gooi weg". stopRecording wordt door
  // de MediaRecorder onstop-callback aangeroepen; we lezen 'm daar om
  // te beslissen of we de blob uploaden of in de prullenbak gooien.
  const recordCancelRef = useRef(false);
  const MAX_VOICE_SECONDS = 5 * 60; // 5 min hard cap — past binnen de 25 MB upload-limiet bij Opus 32 kbps.

  // Locatie delen
  const [locationBusy, setLocationBusy] = useState(false);
  const [locationError, setLocationError] = useState<string | null>(null);
  // Poll-modal
  const [pollOpen, setPollOpen] = useState(false);
  // AttachmentSheet (camera/bestand/poll/locatie achter de paperclip).
  // Twee refs zodat we de file-input + camera-input via 'n nieuwe action
  // direct kunnen activeren — die zaten eerst in een <label> maar zonder
  // visuele triggerknop is een ref-gestuurd input.click() makkelijker.
  const [sheetOpen, setSheetOpen] = useState(false);
  const filePickRef = useRef<HTMLInputElement>(null);
  const cameraPickRef = useRef<HTMLInputElement>(null);

  // Sluit de attachment-sheet zodra de gebruiker in de stream begint te
  // scrollen — anders blijft 'n per ongeluk geopende sheet boven 'n
  // bericht hangen tijdens 't lezen. MessageStream's onScroll dispatchet
  // een custom event op window; iedereen die er om geeft luistert.
  useEffect(() => {
    if (!sheetOpen) return;
    const onScroll = () => setSheetOpen(false);
    window.addEventListener("swift:stream-scroll", onScroll);
    return () => window.removeEventListener("swift:stream-scroll", onScroll);
  }, [sheetOpen]);

  function insertAtCursor(snippet: string) {
    const el = textRef.current;
    if (!el) {
      setText((t) => t + snippet);
      return;
    }
    const start = el.selectionStart ?? text.length;
    const end = el.selectionEnd ?? text.length;
    const next = text.slice(0, start) + snippet + text.slice(end);
    setText(next);
    // verplaats cursor naar achter de ingevoegde emoji
    requestAnimationFrame(() => {
      el.focus();
      const pos = start + snippet.length;
      el.setSelectionRange(pos, pos);
    });
  }
  const typingRef = useRef<{ active: boolean; timeout: ReturnType<typeof setTimeout> | null }>({
    active: false,
    timeout: null,
  });

  useEffect(() => {
    setText("");
    setPending([]);
    textRef.current?.focus();
  }, [conversation.id]);

  useEffect(() => {
    if (replyTarget) textRef.current?.focus();
  }, [replyTarget?.messageId]);

  useEffect(() => {
    const el = textRef.current;
    if (!el) return;
    el.style.height = "auto";
    const lineHeight = parseFloat(getComputedStyle(el).lineHeight) || 22;
    const minHeight = lineHeight + 20; // 1 regel + verticale padding (py-2.5)
    const maxHeight = lineHeight * 5 + 20; // max 5 regels
    // scrollHeight kan 0 zijn vlak na mount; klem 'm naar minHeight zodat
    // 't textarea nooit "verdwijnt" tot een onleesbare strook.
    const target = Math.max(minHeight, Math.min(el.scrollHeight, maxHeight));
    el.style.height = `${target}px`;
    el.style.overflowY = el.scrollHeight > maxHeight ? "auto" : "hidden";
  }, [text]);

  function emitTyping(active: boolean) {
    const s = getSocket();
    if (!s) return;
    if (active && !typingRef.current.active) {
      typingRef.current.active = true;
      s.emit("typing:start", { conversation_id: conversation.id });
    }
    if (!active && typingRef.current.active) {
      typingRef.current.active = false;
      s.emit("typing:stop", { conversation_id: conversation.id });
    }
  }

  function onChange(v: string) {
    /*
     * Getypte smiley meteen omzetten naar een emoji. Hier en niet bij het
     * versturen, zodat je ziet wat je stuurt - en het nog kunt weghalen als het
     * niet was wat je bedoelde.
     *
     * De cursor moet daarna handmatig terug: door de waarde te vervangen zet de
     * browser hem aan het eind van de tekst, en dan spring je midden in een zin
     * ineens naar achteren.
     */
    const el = textRef.current;
    const omgezet = vervangSmiley(v, el?.selectionStart ?? v.length);
    if (omgezet) {
      setText(omgezet.tekst);
      requestAnimationFrame(() => {
        el?.setSelectionRange(omgezet.cursor, omgezet.cursor);
      });
      detectMentionTrigger(omgezet.tekst);
      return;
    }

    setText(v);
    if (v.trim().length > 0) {
      emitTyping(true);
      if (typingRef.current.timeout) clearTimeout(typingRef.current.timeout);
      typingRef.current.timeout = setTimeout(() => emitTyping(false), 3000);
    } else {
      emitTyping(false);
    }
    detectMentionTrigger(v);
  }

  /** Bekijkt de string vlak vóór de cursor: zit er een `@woord`-token
   *  zonder spatie ertussen, dan triggeren we de autocomplete. Zo niet,
   *  sluiten we 'm. Wordt aangeroepen op typen én op cursor-bewegingen. */
  function detectMentionTrigger(v: string) {
    const el = textRef.current;
    const cursor = el?.selectionStart ?? v.length;
    const upto = v.slice(0, cursor);
    // Match een @-token aan het eind: óf aan begin van string, óf met
    // whitespace ervoor. Alfanumeriek + underscore in de naam.
    const m = upto.match(/(?:^|\s)@([A-Za-z0-9_]*)$/);
    if (!m) {
      if (mentionAt !== null) setMentionAt(null);
      return;
    }
    setMentionAt(cursor - m[1]!.length - 1); // index van de @
    setMentionQuery(m[1]!.toLowerCase());
    setMentionIdx(0);
  }

  function pickMention(c: MentionCandidate) {
    if (mentionAt === null) return;
    const token = c.kind === "user" ? c.user.username : c.token;
    const before = text.slice(0, mentionAt);
    const after = text.slice(textRef.current?.selectionStart ?? text.length);
    const insert = `@${token} `;
    const next = before + insert + after;
    setText(next);
    setMentionAt(null);
    setMentionQuery("");
    setMentionList([]);
    requestAnimationFrame(() => {
      const el = textRef.current;
      if (!el) return;
      el.focus();
      const pos = before.length + insert.length;
      el.setSelectionRange(pos, pos);
    });
  }

  /** Start een MediaRecorder-sessie. getUserMedia triggert de browser-/
   *  Android-permission-prompt (eenmalig). Bij verlening: zet recorder op
   *  audio/webm-Opus (Chrome/Edge/Capacitor) of de browser-default
   *  (Safari/iOS). Een interval-tick van 1 s update de timer-knop en
   *  stopt automatisch bij MAX_VOICE_SECONDS. */
  async function startRecording() {
    setRecordError(null);
    if (typeof navigator === "undefined" || !navigator.mediaDevices?.getUserMedia) {
      setRecordError("Browser ondersteunt geen opname");
      return;
    }
    let stream: MediaStream;
    try {
      stream = await navigator.mediaDevices.getUserMedia({ audio: true });
    } catch {
      setRecordError("Microfoon-toegang geweigerd");
      return;
    }
    const preferred = ["audio/webm;codecs=opus", "audio/webm", "audio/mp4"];
    const mime =
      typeof MediaRecorder !== "undefined"
        ? preferred.find((t) => MediaRecorder.isTypeSupported(t)) ?? ""
        : "";
    const recorder = mime ? new MediaRecorder(stream, { mimeType: mime }) : new MediaRecorder(stream);

    recordChunksRef.current = [];
    recordCancelRef.current = false;
    recorder.ondataavailable = (e) => {
      if (e.data.size > 0) recordChunksRef.current.push(e.data);
    };
    recorder.onstop = () => {
      const wasCancelled = recordCancelRef.current;
      const chunks = recordChunksRef.current.slice();
      const usedMime = recorder.mimeType || mime || "audio/webm";
      recordChunksRef.current = [];
      stopStreamAndTimer();
      setRecording(false);
      setRecordSeconds(0);
      if (wasCancelled || chunks.length === 0) return;
      const blob = new Blob(chunks, { type: usedMime });
      const ext = usedMime.includes("mp4") ? "m4a" : "webm";
      const stamp = new Date().toISOString().slice(0, 19).replace(/[:T]/g, "-");
      const file = new File([blob], `spraakbericht-${stamp}.${ext}`, { type: usedMime });
      void addFiles([file]);
    };

    recordStreamRef.current = stream;
    recorderRef.current = recorder;
    recorder.start();
    setRecording(true);
    setRecordSeconds(0);
    recordTimerRef.current = setInterval(() => {
      setRecordSeconds((s) => {
        const next = s + 1;
        if (next >= MAX_VOICE_SECONDS) {
          // Hard cap — gebruiker krijgt de opgenomen seconden alsnog
          // als bijlage (geen cancel-flag), maar de opname stopt nu.
          stopRecording(false);
        }
        return next;
      });
    }, 1000);
  }

  function stopRecording(cancel: boolean) {
    recordCancelRef.current = cancel;
    const r = recorderRef.current;
    if (r && r.state !== "inactive") {
      r.stop();
    } else {
      // Recorder was niet meer actief (race) — toch stream + timer netjes
      // opruimen zodat de microfoon-LED uitgaat.
      stopStreamAndTimer();
      setRecording(false);
      setRecordSeconds(0);
    }
  }

  function stopStreamAndTimer() {
    if (recordTimerRef.current) {
      clearInterval(recordTimerRef.current);
      recordTimerRef.current = null;
    }
    const s = recordStreamRef.current;
    if (s) {
      for (const t of s.getTracks()) t.stop();
      recordStreamRef.current = null;
    }
    recorderRef.current = null;
  }

  useEffect(() => {
    // Cleanup bij conversatie-switch of unmount — laat geen microfoon-
    // stream lopen.
    return () => stopStreamAndTimer();
  }, [conversation.id]);

  /** Vraagt de geolocation-permissie en stuurt een bericht met de
   *  huidige positie als locatie-payload. Werkt op alle drie de
   *  platforms (PWA/Tauri/Capacitor) via dezelfde Web-API; Capacitor's
   *  WebView triggert de Android-runtime-prompt zodra getCurrentPosition
   *  draait — net als de microfoonpermissie bij voice messages. */
  async function shareLocation() {
    setLocationError(null);
    if (typeof navigator === "undefined" || !navigator.geolocation) {
      setLocationError("Geolocatie niet beschikbaar");
      return;
    }
    setLocationBusy(true);
    try {
      const pos = await new Promise<GeolocationPosition>((resolve, reject) => {
        navigator.geolocation.getCurrentPosition(resolve, reject, {
          enableHighAccuracy: true,
          timeout: 10_000,
          maximumAge: 0,
        });
      });
      const body = text.trim();
      const labelPart = body.length > 0 ? body : null;
      try {
        await api.sendMessage(conversation.id, {
          body,
          attachment_ids: pending.map((p) => p.id),
          reply_to_message_id: replyTarget?.messageId,
          location: {
            lat: pos.coords.latitude,
            lng: pos.coords.longitude,
            label: labelPart,
          },
        });
        setText("");
        setPending([]);
        setReplyTarget(conversation.id, null);
      } catch {
        setLocationError("Versturen mislukt");
      }
    } catch (err) {
      const code =
        typeof err === "object" && err !== null && "code" in err
          ? (err as { code: number }).code
          : 0;
      setLocationError(
        code === 1 ? "Geen toestemming voor locatie" : "Locatie niet beschikbaar",
      );
    } finally {
      setLocationBusy(false);
    }
  }

  async function send() {
    if (sendingRef.current) return;
    const body = text.trim();
    if (!body && pending.length === 0) return;
    sendingRef.current = true;
    setSending(true);
    emitTyping(false);
    const replyToId = replyTarget?.messageId;
    try {
      await api.sendMessage(conversation.id, {
        body,
        attachment_ids: pending.map((p) => p.id),
        reply_to_message_id: replyToId,
      });
      setText("");
      setPending([]);
      setReplyTarget(conversation.id, null);
    } catch (err) {
      // Geen netwerk? Berichten zonder bijlagen gaan in de outbox en
      // versturen vanzelf bij reconnect. Met bijlagen kunnen we niet
      // queue'n (V1 — Blob-cache nog niet) — gebruiker houdt z'n
      // composer-inhoud zodat 'ie 't later kan herproberen.
      if (isQueueableError(err) && pending.length === 0 && body.length > 0) {
        await pushOutbox({
          id: makeOutboxId(),
          conversation_id: conversation.id,
          body,
          reply_to_message_id: replyToId,
          created_at: Date.now(),
          attempt_count: 0,
        });
        useStore.getState().setOutboxItems(await listOutbox());
        setText("");
        setReplyTarget(conversation.id, null);
      } else {
        // Echte server-fout of fout-met-bijlagen — re-throw zodat de
        // dev-console 'm logt; composer-inhoud blijft staan.
        throw err;
      }
    } finally {
      sendingRef.current = false;
      setSending(false);
      textRef.current?.focus();
    }
  }

  async function addFiles(files: File[]) {
    if (files.length === 0) return;
    setUploading(true);
    try {
      const uploaded: PendingAttachment[] = [];
      for (const f of files) {
        const { attachment } = await uploadFile(f);
        uploaded.push({ id: attachment.id, filename: attachment.filename, size: attachment.size });
      }
      setPending((p) => [...p, ...uploaded]);
    } finally {
      setUploading(false);
    }
  }

  async function onPickFiles(files: FileList | null) {
    if (!files) return;
    await addFiles(Array.from(files));
  }

  // Drag-and-drop in de conversatie-pane (Main.tsx) voedt bestanden via
  // deze ref-handle in dezelfde pending-flow als de 📎-knop. Android share-
  // sheet kan tekst/links via dezelfde ref in de composer plaatsen.
  useImperativeHandle(
    ref,
    () => ({
      addFiles,
      insertText: (sharedText: string) => insertAtCursor(sharedText),
    }),
    [conversation.id, text],
  );

  function onKeyDown(e: React.KeyboardEvent<HTMLTextAreaElement>) {
    // Mention-popover heeft voorrang: pijltjes en Enter sturen 'm aan
    // i.p.v. de message-send / textarea-default.
    if (mentionOpen) {
      if (e.key === "ArrowDown") {
        e.preventDefault();
        setMentionIdx((i) => (i + 1) % mentionList.length);
        return;
      }
      if (e.key === "ArrowUp") {
        e.preventDefault();
        setMentionIdx((i) => (i - 1 + mentionList.length) % mentionList.length);
        return;
      }
      if (e.key === "Enter" || e.key === "Tab") {
        e.preventDefault();
        const pick = mentionList[mentionIdx];
        if (pick) pickMention(pick);
        return;
      }
      if (e.key === "Escape") {
        e.preventDefault();
        setMentionAt(null);
        return;
      }
    }
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      void send();
    }
  }

  // Ctrl+V van een afbeelding (bv. een screenshot uit Snipping Tool) →
  // upload 'm als bijlage. Geplakte tekst laten we ongemoeid (default).
  function onPaste(e: React.ClipboardEvent<HTMLTextAreaElement>) {
    if (blocked) return;
    const files: File[] = [];
    for (const item of Array.from(e.clipboardData.items)) {
      if (item.kind === "file" && item.type.startsWith("image/")) {
        const f = item.getAsFile();
        if (f) {
          // Klembord-afbeeldingen heten vaak "image.png"; geef ze een
          // unieke naam met tijdstempel zodat ze herkenbaar zijn.
          const ext = (f.type.split("/")[1] || "png").replace(/[^a-z0-9]/gi, "");
          const stamp = new Date().toISOString().slice(0, 19).replace(/[:T]/g, "-");
          files.push(new File([f], `plakken-${stamp}.${ext}`, { type: f.type }));
        }
      }
    }
    if (files.length > 0) {
      e.preventDefault();
      void addFiles(files);
    }
  }

  const blocked = Boolean(disabledReason);

  return (
    <div
      className="relative border-t border-slate-700 bg-slate-800 px-1.5 pt-1 sm:p-2"
      // Onderkant respecteren — op iOS valt 'm anders onder de
      // home-indicator. `pb-safe` is de Tailwind-utility uit onze config.
      style={{ paddingBottom: "max(0.5rem, env(safe-area-inset-bottom))" }}
    >
      {disabledReason && (
        <div className="mb-2 rounded-md border border-amber-700 bg-amber-950/40 px-3 py-2 text-xs text-amber-200">
          {disabledReason}
        </div>
      )}
      {!blocked && emojiOpen && (
        <EmojiPicker
          onPick={(e) => {
            insertAtCursor(e);
            setEmojiOpen(false);
          }}
          onClose={() => setEmojiOpen(false)}
        />
      )}
      {!blocked && mentionAt !== null && (
        <MentionAutocomplete
          conversation={conversation}
          query={mentionQuery}
          selectedIndex={mentionIdx}
          onPick={pickMention}
          onCandidatesChanged={setMentionList}
        />
      )}
      {replyTarget && (
        <div className="mb-2 flex items-start gap-2 rounded-md border-l-4 border-accent bg-slate-700/50 px-3 py-1.5 text-xs">
          <div className="min-w-0 flex-1">
            <div className="font-medium text-accent">
              Antwoord op {replyTarget.senderName}
            </div>
            <div className="truncate text-slate-300">
              {replyTarget.preview || <em className="text-slate-500">[geen tekst]</em>}
            </div>
          </div>
          <button
            onClick={() => setReplyTarget(conversation.id, null)}
            className="shrink-0 rounded p-0.5 text-slate-400 hover:bg-slate-600 hover:text-slate-100"
            title="Antwoord annuleren"
            aria-label="Antwoord annuleren"
          >
            <IconClose size={14} />
          </button>
        </div>
      )}
      {pending.length > 0 && (
        <div className="mb-2 flex flex-wrap gap-2">
          {pending.map((p) => (
            <span
              key={p.id}
              className="flex items-center gap-1 rounded bg-slate-700 px-2 py-0.5 text-xs text-slate-200"
            >
              <IconPaperclip size={12} className="text-slate-400" />
              <span className="truncate max-w-[12rem]">{p.filename}</span>
              <button
                className="ml-1 text-slate-400 hover:text-red-400"
                onClick={() => setPending((x) => x.filter((y) => y.id !== p.id))}
                aria-label="Verwijderen"
              >
                <IconClose size={11} />
              </button>
            </span>
          ))}
        </div>
      )}
      {recording && (
        <div className="mb-2 flex items-center gap-2 rounded-md border border-red-700 bg-red-950/40 px-3 py-2 text-xs text-red-200">
          <span className="h-2 w-2 animate-pulse rounded-full bg-red-500" aria-hidden="true" />
          <span className="flex-1">
            Spraakbericht aan het opnemen — {formatSeconds(recordSeconds)}
          </span>
          <button
            onClick={() => stopRecording(true)}
            className="rounded px-2 py-0.5 text-slate-300 hover:bg-slate-700 hover:text-white"
            title="Annuleren"
          >
            ✕ Annuleer
          </button>
          <button
            onClick={() => stopRecording(false)}
            className="rounded bg-red-600 px-2 py-0.5 font-medium text-white hover:bg-red-500"
            title="Opname stoppen en als bijlage toevoegen"
          >
            ■ Stop
          </button>
        </div>
      )}
      {recordError && !recording && (
        <div className="mb-2 rounded-md border border-amber-700 bg-amber-950/40 px-3 py-1 text-xs text-amber-200">
          {recordError}
        </div>
      )}
      {locationError && !locationBusy && (
        <div className="mb-2 rounded-md border border-amber-700 bg-amber-950/40 px-3 py-1 text-xs text-amber-200">
          {locationError}
        </div>
      )}
      {pollOpen && (
        <PollComposer
          conversation={conversation}
          onClose={() => setPollOpen(false)}
        />
      )}
      {/* Verborgen file-inputs: action-sheet activeert ze via ref.click().
          Apart voor camera (capture-attr) en gewone bestanden zodat we niet
          tussen modi hoeven te toggle'n. */}
      <input
        ref={cameraPickRef}
        type="file"
        accept="image/*"
        capture="environment"
        className="hidden"
        onChange={(e) => {
          void onPickFiles(e.target.files);
          e.target.value = "";
        }}
      />
      <input
        ref={filePickRef}
        type="file"
        multiple
        className="hidden"
        onChange={(e) => {
          void onPickFiles(e.target.files);
          e.target.value = "";
        }}
      />
      {sheetOpen && (
        <AttachmentSheet
          actions={[
            {
              key: "camera",
              label: "Foto maken",
              icon: <IconCamera />,
              onPick: () => cameraPickRef.current?.click(),
              available: isTouchPlatform(),
            },
            {
              key: "file",
              label: "Bestand kiezen",
              icon: <IconFile />,
              onPick: () => filePickRef.current?.click(),
              available: true,
            },
            {
              key: "poll",
              label: "Poll aanmaken",
              icon: <IconPoll />,
              onPick: () => setPollOpen(true),
              available: conversation.kind === "channel",
            },
            {
              key: "location",
              label: locationBusy ? "Bezig met locatie ophalen…" : "Mijn locatie delen",
              icon: <IconLocation />,
              onPick: () => void shareLocation(),
              available:
                typeof navigator !== "undefined" &&
                navigator.geolocation !== undefined,
            },
          ]}
          onClose={() => setSheetOpen(false)}
        />
      )}
      <div className="flex items-end gap-0.5 rounded-2xl bg-slate-800 px-1 py-1 sm:gap-1">
        <button
          type="button"
          onClick={() => setEmojiOpen((o) => !o)}
          disabled={blocked}
          className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-slate-300 hover:bg-slate-700 hover:text-slate-100 disabled:opacity-50"
          title="Emoji invoegen"
          aria-label="Emoji invoegen"
        >
          <IconSmile size={20} />
        </button>
        <button
          type="button"
          onClick={() => setSheetOpen((o) => !o)}
          disabled={blocked || sending || recording}
          className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-full disabled:opacity-50 ${
            sheetOpen
              ? "bg-slate-700 text-slate-100"
              : "text-slate-300 hover:bg-slate-700 hover:text-slate-100"
          }`}
          title="Toevoegen"
          aria-label="Toevoegen (camera, bestand, poll, locatie)"
          aria-expanded={sheetOpen}
        >
          <IconPaperclip size={20} />
        </button>
        <textarea
          ref={textRef}
          className="min-w-0 flex-1 resize-none rounded-2xl border border-slate-700 bg-slate-900 px-3 py-2.5 text-base leading-snug text-slate-100 outline-none focus:border-accent disabled:cursor-not-allowed disabled:opacity-60 sm:text-sm"
          rows={1}
          value={text}
          onChange={(e) => onChange(e.target.value)}
          onKeyDown={onKeyDown}
          onKeyUp={() => detectMentionTrigger(text)}
          onClick={() => detectMentionTrigger(text)}
          onBlur={() => setMentionAt(null)}
          onPaste={onPaste}
          disabled={blocked}
          /* Kort houden. Het veld is één regel hoog en groeit niet mee met een
             plaatshouder, dus een langere zin wordt onderaan afgesneden - precies
             wat er gebeurde met "Sturen kan niet — gebruiker is offline". De reden
             staat toch al voluit in de balk er direct boven. */
          placeholder={blocked ? "Sturen kan niet" : "Bericht"}
        />
        <button
          type="button"
          onClick={() => (recording ? stopRecording(false) : void startRecording())}
          disabled={blocked || sending}
          className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-full disabled:opacity-50 ${
            recording
              ? "bg-red-600 text-white hover:bg-red-500"
              : "text-slate-300 hover:bg-slate-700 hover:text-slate-100"
          }`}
          title={recording ? "Opname stoppen" : "Spraakbericht opnemen"}
          aria-label={recording ? "Opname stoppen" : "Spraakbericht opnemen"}
        >
          {recording ? <IconStop size={14} /> : <IconMic size={20} />}
        </button>
        <button
          onClick={send}
          disabled={
            blocked || sending || uploading || recording || (text.trim().length === 0 && pending.length === 0)
          }
          className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-accent p-0 shadow-lg shadow-accent/20 ring-1 ring-white/10 hover:bg-accent-hover disabled:opacity-50"
          title="Versturen"
          aria-label="Versturen"
        >
          {sending ? (
            <span className="text-lg font-semibold text-white">…</span>
          ) : (
            <IconSend size={18} />
          )}
        </button>
      </div>
      {uploading && <div className="mt-1 text-xs text-slate-500">Bezig met uploaden…</div>}
    </div>
  );
});

export default Composer;
